import { defineMessage } from 'react-intl'

import { cloneDeep, isEqual } from 'lodash-es'

import {
  CountryPreferenceConfig,
  MEMBER_PREFERENCE_FEATURE_FLAGS,
  OPTIONS_TYPE,
  PreferenceMetadataConfig,
  RENDER_CONDITION_TARGETS,
} from './types'
import { CountryNameToISOCode } from '../../intl/constants'
import {
  MEMBER_PREFERENCE_IDS,
  MEMBER_PREFERENCE_NAMES,
  S4C_PROGRAM_OPTION_FLOW,
} from '../../memberPreferences/constants'
import { MEMBER_PREFERENCE_LABELS } from '../../searchForCare/messages'

const optionsProgressive = [
  {
    text: MEMBER_PREFERENCE_LABELS['LGBTQIA+'],
    id: MEMBER_PREFERENCE_IDS['LGBTQIA+'],
  },
]

const optionsConservative = [
  {
    text: MEMBER_PREFERENCE_LABELS.experienceSupportingTheLGBTQIACommunity,
    id: MEMBER_PREFERENCE_IDS['LGBTQIA+'],
    selectedTextLabel: MEMBER_PREFERENCE_LABELS.lgbtqiaExperience,
  },
]

const footerTextProgressive = {
  message: defineMessage({
    defaultMessage:
      'We’ll prioritize finding you providers who are LGBTQIA+. We’ll also show you providers with experience supporting the LGBTQIA+ community.',
    description:
      'Description text clarifying to users that selecting an option here will show experienced providers with the LGBTQIA+ community',
  }),
  renderConditions: [
    {
      target: RENDER_CONDITION_TARGETS.SELECTED_PREFERENCES,
      path: 'isLgbtqia',
      fn: (val: any) => isEqual(val, [MEMBER_PREFERENCE_IDS['LGBTQIA+']]),
    },
  ],
  color: 'textInactive',
}

const countryConfigProgressive = {
  programOptionFlows: [S4C_PROGRAM_OPTION_FLOW.THERAPY],
  options: optionsProgressive,
  footerText: footerTextProgressive,
  promptText: defineMessage({
    defaultMessage: 'I prefer a provider who identifies as:',
    description: 'Text prompting the user to select options they associate with',
  }),
}

const countryConfigConservative: CountryPreferenceConfig = {
  programOptionFlows: [S4C_PROGRAM_OPTION_FLOW.THERAPY],
  options: optionsConservative,
}

const countryConfigConservativeFeatureFlagged = cloneDeep(countryConfigConservative)
countryConfigConservativeFeatureFlagged.requiredFeatureFlag =
  MEMBER_PREFERENCE_FEATURE_FLAGS.CONSERVATIVE_GLOBAL_PREFERENCES

const lgbtqiaPreference: PreferenceMetadataConfig = {
  name: MEMBER_PREFERENCE_NAMES.IS_LGBTQIA,
  title: MEMBER_PREFERENCE_LABELS['LGBTQIA+'],
  optionType: OPTIONS_TYPE.CHECKBOX,
  icon: 'MemberPreferenceLGBTQIAIllustrationIcon',
  promptText: defineMessage({
    defaultMessage: 'I prefer a provider who has:',
    description: 'Text prompting the user to select the experience supporting the LGBTQIA+ community option',
  }),

  countryConfig: {
    [CountryNameToISOCode.Argentina]: countryConfigConservative,
    [CountryNameToISOCode.Australia]: countryConfigProgressive,
    [CountryNameToISOCode.Austria]: countryConfigConservative,
    [CountryNameToISOCode.Belgium]: countryConfigConservative,
    [CountryNameToISOCode.Brazil]: countryConfigProgressive,
    [CountryNameToISOCode.Bulgaria]: countryConfigConservativeFeatureFlagged,
    [CountryNameToISOCode.Canada]: countryConfigProgressive,
    [CountryNameToISOCode.Chile]: countryConfigConservative,
    [CountryNameToISOCode.Colombia]: countryConfigConservative,
    [CountryNameToISOCode.Croatia]: countryConfigConservative,
    [CountryNameToISOCode.CzechRepublic]: countryConfigConservative,
    [CountryNameToISOCode.Denmark]: countryConfigConservative,
    [CountryNameToISOCode.Egypt]: countryConfigConservative,
    [CountryNameToISOCode.ElSalvador]: countryConfigConservativeFeatureFlagged,
    [CountryNameToISOCode.Fiji]: countryConfigConservativeFeatureFlagged,
    [CountryNameToISOCode.Finland]: countryConfigConservative,
    [CountryNameToISOCode.France]: countryConfigConservative,
    [CountryNameToISOCode.Germany]: countryConfigConservative,
    [CountryNameToISOCode.Ghana]: countryConfigConservative,
    [CountryNameToISOCode.Greece]: countryConfigConservative,
    [CountryNameToISOCode.HongKong]: countryConfigConservative,
    [CountryNameToISOCode.Hungary]: countryConfigConservative,
    [CountryNameToISOCode.Iceland]: countryConfigConservative,
    [CountryNameToISOCode.India]: countryConfigConservative,
    [CountryNameToISOCode.Indonesia]: countryConfigConservative,
    [CountryNameToISOCode.Ireland]: countryConfigProgressive,
    [CountryNameToISOCode.Israel]: countryConfigProgressive,
    [CountryNameToISOCode.Italy]: countryConfigConservative,
    [CountryNameToISOCode.Japan]: countryConfigConservative,
    [CountryNameToISOCode.Kenya]: countryConfigConservative,
    [CountryNameToISOCode.Kuwait]: countryConfigConservativeFeatureFlagged,
    [CountryNameToISOCode.Lithuania]: countryConfigConservative,
    [CountryNameToISOCode.Luxembourg]: countryConfigConservativeFeatureFlagged,
    [CountryNameToISOCode.Macao]: countryConfigConservativeFeatureFlagged,
    [CountryNameToISOCode.Malaysia]: countryConfigConservative,
    [CountryNameToISOCode.Mexico]: countryConfigConservative,
    [CountryNameToISOCode.Netherlands]: countryConfigProgressive,
    [CountryNameToISOCode.NewZealand]: countryConfigProgressive,
    [CountryNameToISOCode.Nigeria]: countryConfigConservative,
    [CountryNameToISOCode.Norway]: countryConfigConservative,
    [CountryNameToISOCode.Peru]: countryConfigConservative,
    [CountryNameToISOCode.Philippines]: countryConfigConservative,
    [CountryNameToISOCode.Poland]: countryConfigConservative,
    [CountryNameToISOCode.Portugal]: countryConfigConservative,
    [CountryNameToISOCode.Qatar]: countryConfigConservative,
    [CountryNameToISOCode.RepublicOfKorea]: countryConfigConservative,
    [CountryNameToISOCode.Romania]: countryConfigConservative,
    [CountryNameToISOCode.SaudiArabia]: countryConfigConservative,
    [CountryNameToISOCode.Senegal]: countryConfigConservativeFeatureFlagged,
    [CountryNameToISOCode.Singapore]: countryConfigConservative,
    [CountryNameToISOCode.Slovakia]: countryConfigConservative,
    [CountryNameToISOCode.SouthAfrica]: countryConfigProgressive,
    [CountryNameToISOCode.Spain]: countryConfigConservative,
    [CountryNameToISOCode.Sweden]: countryConfigConservative,
    [CountryNameToISOCode.Switzerland]: countryConfigConservative,
    [CountryNameToISOCode.Taiwan]: countryConfigProgressive,
    [CountryNameToISOCode.Thailand]: countryConfigConservative,
    [CountryNameToISOCode.Türkiye]: countryConfigConservative,
    [CountryNameToISOCode.Ukraine]: countryConfigConservative,
    [CountryNameToISOCode.UnitedArabEmirates]: countryConfigConservative,
    [CountryNameToISOCode.UnitedKingdom]: countryConfigProgressive,
    [CountryNameToISOCode.UnitedStatesOfAmerica]: {
      ...countryConfigProgressive,
      programOptionFlows: [
        S4C_PROGRAM_OPTION_FLOW.COACHING,
        S4C_PROGRAM_OPTION_FLOW.COACHING_WITH_LMS,
        S4C_PROGRAM_OPTION_FLOW.THERAPY,
      ],
    },
    [CountryNameToISOCode.Uruguay]: countryConfigConservativeFeatureFlagged,
    [CountryNameToISOCode.VietNam]: countryConfigConservative,
  },
}

export default lgbtqiaPreference
